import { createFileRoute, Link } from "@tanstack/react-router";
import { SETTING_OUTPATIENT_ES } from "@/lib/medical-schema";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { SectionCta } from "@/components/site/SectionCta";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const URL = `${SITE}/es/gastroplastia-endoscopica-en-manga`;

/** Spanish translation of the English ESG FAQ, kept in sync with /services/esg. */
const FAQS = [
  {
    q: "¿Cuánto peso puedo bajar con la ESG?",
    a: "Los estudios publicados informan una pérdida promedio del 13 al 17 por ciento del peso corporal total entre los 12 y los 24 meses, y muchos pacientes mantienen la mayor parte de esa pérdida a largo plazo cuando siguen el plan de nutrición y actividad física.",
  },
  {
    q: "¿La ESG es reversible?",
    a: "La ESG conserva el órgano: no se extrae ninguna parte del estómago, y las suturas se pueden retirar o revisar por endoscopia, de modo que las opciones de tratamiento futuras quedan abiertas.",
  },
  {
    q: "¿Cuánto dura la recuperación después de la ESG?",
    a: "La mayoría de los pacientes vuelve a casa el mismo día o después de una sola noche, retoma el trabajo de oficina y la actividad ligera en pocos días, y sigue una dieta por etapas que va de líquidos claros a alimentos sólidos en unas siete semanas.",
  },
  {
    q: "¿Quién es candidato para la ESG?",
    a: "La ESG suele ofrecerse a adultos con un IMC aproximado de 30 a 40 que no han logrado resultados duraderos con dieta y ejercicio, o a pacientes con obesidad más severa que desean evitar la cirugía convencional. La idoneidad depende también de la historia clínica y de los intentos previos de bajar de peso, no solo del IMC.",
  },
  {
    q: "¿El seguro cubre la ESG?",
    a: "La ESG todavía no tiene una cobertura consistente por parte de los planes comerciales. En la consulta podemos revisar las opciones de pago propio y financiamiento; nuestra guía de cobertura de seguro explica qué procedimientos suelen cubrir los planes principales.",
  },
];

export const Route = createFileRoute("/es/gastroplastia-endoscopica-en-manga")({
  head: () => {
    const base = esHead("esg");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("esg"),
              {
                "@type": "MedicalProcedure",
                "@id": `${URL}#procedimiento`,
                name: "Gastroplastia endoscópica en manga (ESG)",
                alternateName: ["Procedimiento de acordeón", "Endoscopic sleeve gastroplasty"],
                description:
                  "Procedimiento endoscópico de pérdida de peso, sin incisiones, que remodela el estómago con suturas internas colocadas a través de la boca.",
                bodyLocation: "estómago",
                procedureType: "https://schema.org/NoninvasiveProcedure",
                howPerformed:
                  "Se realiza a través de la boca con un endoscopio flexible y un dispositivo de sutura endoscópica. No hay incisiones externas." + " " + SETTING_OUTPATIENT_ES,
                preparation: "Anestesia general; ayuno desde la noche anterior al procedimiento.",
                followup:
                  "Dieta por etapas que avanza de líquidos claros a sólidos en unas siete semanas; seguimiento regular con la nutricionista y el cirujano.",
                url: URL,
                recognizingAuthority: { "@id": `${SITE}/#organization` },
              },
              {
                "@type": "FAQPage",
                "@id": `${URL}#preguntas`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${URL}#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsEsg,
});

function EsEsg() {
  return (
    <EsPageShell
      page="esg"
      title="Gastroplastia endoscópica en manga (ESG) en el norte de Virginia"
      intro="La ESG es un procedimiento de pérdida de peso sin incisiones, realizado por completo a través de la boca, que remodela el estómago con suturas internas. Es ambulatorio y no se extrae ninguna parte del estómago."
    >
      <EsSection id="que-es" heading="Qué es la gastroplastia endoscópica en manga">
        <p>
          La gastroplastia endoscópica en manga (ESG), a veces llamada «procedimiento de
          acordeón», es un procedimiento de pérdida de peso sin incisiones que se realiza
          completamente a través de la boca. Con un endoscopio flexible equipado con un
          dispositivo de sutura, el médico coloca una serie de puntos a lo largo del estómago
          para plegarlo y darle forma de tubo estrecho, reduciendo su volumen entre un 70 y un 80
          por ciento, sin extraer tejido ni hacer una sola incisión externa.
        </p>
        <p>
          El resultado es que usted se llena antes, come porciones más pequeñas y, para muchos
          pacientes, logra una pérdida de peso importante y duradera.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 rounded-lg border border-border p-4 sm:grid-cols-3">
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-mid">13–17 %</p>
            <p className="mt-1 text-sm text-foreground/70">
              del peso corporal total perdido, en promedio, entre los 12 y los 24 meses
            </p>
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-mid">~2 %</p>
            <p className="mt-1 text-sm text-foreground/70">
              tasa de eventos adversos graves
            </p>
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-mid">Sin incisiones</p>
            <p className="mt-1 text-sm text-foreground/70">
              se realiza por completo a través de la boca
            </p>
          </div>
        </div>
      </EsSection>

      <EsSection id="como-funciona" heading="En qué consiste el procedimiento">
        <p>
          La ESG se realiza bajo anestesia general y suele durar entre 45 y 90 minutos. La
          mayoría de los pacientes vuelve a casa el mismo día o después de una sola noche. El
          endoscopio (un gastroscopio) se pasa por el esófago hasta el estómago, donde un sistema
          de sutura permite colocar puntos de espesor completo que pliegan el estómago hacia
          adentro. El resultado es un estómago con una forma funcional parecida a la que se logra
          con la gastrectomía en manga quirúrgica.
        </p>
        <p>
          Pero como no se extrae ninguna parte de la anatomía, la ESG conserva el órgano y es, en
          principio, reversible. Y es revisable.
        </p>
      </EsSection>

      <EsSection id="resultados" heading="¿Cuánto peso se pierde con la ESG?">
        <p>
          La evidencia sobre la ESG ha madurado considerablemente. En el estudio aleatorizado
          MERIT, publicado en <em>The Lancet</em> (2022), los pacientes tratados con ESG más
          cambios de estilo de vida perdieron en promedio el 13.6 por ciento de su peso corporal
          total a los 12 meses, frente al 0.8 por ciento con cambios de estilo de vida solos, y el
          68 por ciento de los pacientes mantuvo al menos el 25 por ciento de la pérdida del
          exceso de peso a los dos años. Las complicaciones graves relacionadas con el
          procedimiento ocurrieron en cerca del 2 por ciento de los pacientes, sin muertes y sin
          necesidad de cuidados intensivos ni de cirugía.
        </p>
        <p>
          Estos hallazgos coinciden con los análisis combinados (metaanálisis). Una revisión
          sistemática en <em>Clinical Gastroenterology and Hepatology</em> (2019), con cerca de
          1,800 pacientes, informó una pérdida promedio del peso corporal total de alrededor del
          15 por ciento a los 6 meses, sostenida en aproximadamente 16.5 por ciento a los 12
          meses y 17 por ciento entre los 18 y los 24 meses, con una tasa de eventos adversos
          graves del 2.2 por ciento. En términos prácticos, un paciente de 250 libras podría
          esperar perder más de 50 libras y, lo más importante, mantener la mayor parte de esa
          pérdida con apoyo continuo de estilo de vida.
        </p>
      </EsSection>

      <EsSection id="beneficios" heading="Beneficios de salud más allá del peso">
        <p>
          Como ocurre con la pérdida de peso lograda por cualquier vía, bajar de peso con la ESG
          viene acompañado de una mejoría en las enfermedades relacionadas con la obesidad. En el
          estudio MERIT, el 80 por ciento de los pacientes de ESG mejoró en una o más
          comorbilidades metabólicas, como la diabetes tipo 2, la presión arterial alta, el
          colesterol elevado y la apnea obstructiva del sueño.
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Sin incisiones y conserva el órgano: sin cicatrices externas y sin extracción permanente del estómago</li>
          <li>Salida el mismo día o después de una noche, con un regreso a la actividad normal más rápido que con la cirugía</li>
          <li>Perfil de riesgo menor que el de la cirugía bariátrica laparoscópica en pacientes bien seleccionados</li>
          <li>Potencialmente reversible o revisable, lo que mantiene abiertas las opciones futuras</li>
          <li>El reflujo de nueva aparición es poco frecuente: ocurre menos que después de la manga quirúrgica</li>
        </ul>
      </EsSection>

      <EsSection id="candidatos" heading="¿Quién es candidato para la ESG?">
        <p>
          La ESG suele ofrecerse a adultos con un índice de masa corporal (IMC) aproximado de 30
          a 40 que no han logrado resultados duraderos con dieta y ejercicio. Es especialmente
          adecuada para pacientes con obesidad clase I y clase II, y para quienes tienen obesidad
          más severa y no son candidatos a la cirugía bariátrica convencional, o desean evitarla.
        </p>
        <p>
          Sin embargo, la idoneidad depende de más que el IMC; vea nuestros{" "}
          <Link to="/es/requisitos-para-cirugia" className="font-medium text-brand-logo hover:underline">
            requisitos completos para la cirugía bariátrica
          </Link>{" "}
          con los umbrales de IMC y los criterios de enfermedades asociadas. El Dr. Fitzer le
          explicará cómo su historia clínica, sus intentos previos de bajar de peso y sus patrones
          de alimentación influyen en la idoneidad de la ESG. En pacientes con ciertas
          condiciones, como hernias hiatales grandes, cirugía previa del estómago, úlceras activas
          o ciertos trastornos gastrointestinales, la ESG no es apropiada. Una evaluación completa,
          idealmente dentro de un programa con experiencia y multidisciplinario, es lo que
          determina si la ESG es la opción correcta para usted.
        </p>
      </EsSection>

      <EsSection id="recuperacion" heading="Recuperación y qué esperar">
        <p>
          Las náuseas leves, los cólicos y la molestia abdominal de los primeros días son comunes
          y suelen controlarse con medicamentos. La dieta avanza por etapas: comienza con líquidos
          claros, sigue con líquidos completos y purés, y vuelve gradualmente a los alimentos
          sólidos en unas siete semanas.
        </p>
        <p>
          Es común retomar el trabajo y la actividad ligera en pocos días. Durante el primer año
          después del procedimiento, tendrá orientación nutricional regular con nuestra
          nutricionista y seguimiento con el Dr. Fitzer o su asistente médico. (Un consejo
          gratuito: cualquiera que sea su camino para bajar de peso, haga del seguimiento una
          prioridad. Las investigaciones muestran de manera consistente que un seguimiento sólido
          predice el éxito de la pérdida de peso a largo plazo después de la ESG, y de cualquier
          otro método.)
        </p>
      </EsSection>

      <EsSection id="riesgos" heading="Riesgos y consideraciones">
        <p>
          La ESG tiene un historial de seguridad sólido, pero ningún procedimiento está libre de
          riesgo. Las complicaciones graves son poco frecuentes (aproximadamente entre el 1.5 y el
          2.3 por ciento en la literatura publicada) y pueden incluir sangrado, una fuga o una
          colección de líquido alrededor del estómago o, rara vez, una perforación; la mayoría de
          estos eventos se puede manejar sin cirugía abierta. Los efectos más comunes y temporales
          son náuseas, vómitos y dolor abdominal en los primeros días. La ESG es una herramienta,
          no una cura: es posible recuperar peso si no se mantienen los cambios de estilo de vida
          a largo plazo. Su médico revisará en detalle sus riesgos individuales y los beneficios
          esperados durante la consulta.
        </p>
      </EsSection>

      <EsSection id="esg-vs-manga" heading="ESG frente a la manga gástrica quirúrgica">
        <p>
          La pregunta más común que escuchamos es cómo se compara la ESG con la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica quirúrgica (gastrectomía en manga)
          </Link>{" "}
          laparoscópica tradicional. La manga quirúrgica suele producir una mayor pérdida de peso
          a largo plazo, del orden del 25 al 30 por ciento del peso corporal total, porque se
          extrae de forma permanente una porción más grande del estómago. La ESG, en cambio,
          remodela el estómago sin extraer tejido y produce una pérdida de peso promedio de
          alrededor del 15 al 17 por ciento del peso corporal total.
        </p>
        <p>
          A cambio de un techo más bajo en la pérdida de peso, la ESG ofrece ausencia de
          incisiones externas, una recuperación más corta, una menor tasa de reflujo de nueva
          aparición y una anatomía conservada que mantiene abiertas las opciones futuras. Para
          pacientes con obesidad clase I o clase II, o para quienes no son candidatos a la cirugía
          convencional o desean evitarla, ese intercambio suele ser el correcto.
        </p>
      </EsSection>

      <EsSection id="esg-y-medicamentos" heading="La ESG y los medicamentos para bajar de peso">
        <p>
          Un estudio reciente de JAMA Network Open (Haseeb y cols., 2024) comparó la ESG con la
          semaglutida (Wegovy) y encontró que la ESG produjo una pérdida de peso mayor y más
          duradera a los dos años, con recuperación de peso frecuente después de suspender la
          semaglutida. Para muchos pacientes, la ESG y los medicamentos GLP-1 son herramientas
          complementarias más que competidoras: los medicamentos pueden servir de puente antes del
          procedimiento o reforzar los resultados más adelante. El Dr. Fitzer le explicará cómo
          encajan estas opciones en su situación durante la consulta.
        </p>
      </EsSection>

      <EsSection id="proximos-pasos" heading="Próximos pasos: programe una consulta">
        <p>
          Si está considerando la ESG, el primer paso es una consulta para revisar su historia
          clínica, sus metas y si este procedimiento se ajusta a sus necesidades. Nuestro equipo
          ofrece una evaluación completa y apoyo a largo plazo para ayudarlo a lograr y mantener
          sus resultados.{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            Escríbanos
          </Link>{" "}
          o llame al (703) 709-9771 para programar una cita.
        </p>
        <p>
          ¿No va a usar el seguro? Consulte el{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-esg"
            className="font-medium text-brand-logo hover:underline"
          >
            costo de la ESG
          </Link>{" "}
          de pago propio: $12,900, que cubre el honorario del médico, el cargo del centro
          ambulatorio del INOVA Fair Oaks Hospital, un estimado de anestesia y un año de
          seguimiento de rutina con el Dr. Fitzer.
        </p>
      </EsSection>

      <EsSection id="norte-de-virginia" heading="Gastroplastia endoscópica en manga en el norte de Virginia y el área de D.C.">
        <p>
          La ESG se realiza como procedimiento ambulatorio en INOVA Fair Oaks Hospital en Fairfax. No hay incisiones, y casi todos los pacientes se van a casa el mismo día. Las consultas y el seguimiento son en nuestro consultorio de 171 Elden Street, Herndon. Los pacientes de Arlington, Alexandria y Washington, D.C. pueden comenzar con una visita por telesalud.
        </p>
        <p>
          ¿Está comparando opciones? Vea <Link to="/es/manga-gastrica" className="text-brand-deep underline hover:no-underline">manga gástrica en el Norte de Virginia</Link>, el <Link to="/es/balon-gastrico" className="text-brand-deep underline hover:no-underline">balón gástrico Orbera en el Norte de Virginia</Link> o la <Link to="/es/perdida-de-peso-con-medicamentos" className="text-brand-deep underline hover:no-underline">pérdida de peso con medicamentos en el Norte de Virginia</Link>.
        </p>
        <p>
          ¿Preguntas sobre la ESG? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_esg_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes sobre la ESG">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">
                {f.q === "¿El seguro cubre la ESG?" ? (
                  <>
                    La ESG todavía no tiene una cobertura consistente por parte de los planes
                    comerciales. En la consulta podemos revisar las opciones de pago propio y
                    financiamiento; nuestra{" "}
                    <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
                      guía de cobertura de seguro
                    </Link>{" "}
                    explica qué procedimientos suelen cubrir los planes principales. Si pagará el
                    procedimiento de su bolsillo, nuestra página de{" "}
                    <Link
                      to="/es/cirugia-bariatrica-de-pago-directo"
                      className="font-medium text-brand-logo hover:underline"
                    >
                      cirugía bariátrica de pago propio
                    </Link>{" "}
                    lista el precio publicado de la ESG y lo que incluye.
                  </>
                ) : (
                  f.a
                )}
              </dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="referencias" heading="Referencias">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground/70">
          <li>
            Abu Dayyeh BK, et al. Endoscopic sleeve gastroplasty for treatment of class 1 and 2
            obesity (MERIT). <em>Lancet</em>. 2022;400:441–451. doi:10.1016/S0140-6736(22)01280-6
          </li>
          <li>
            Hedjoudje A, et al. Efficacy and Safety of Endoscopic Sleeve Gastroplasty: A
            Systematic Review and Meta-Analysis. <em>Clin Gastroenterol Hepatol</em>.
            2020;18:1043–1053. doi:10.1016/j.cgh.2019.08.022
          </li>
          <li>
            Li P, et al. Efficacy and safety of endoscopic sleeve gastroplasty for obesity
            patients: a meta-analysis. <em>Surg Endosc</em>. 2020;34:1253–1260.
            doi:10.1007/s00464-019-06889-6
          </li>
          <li>
            Marincola G, et al. Laparoscopic sleeve gastrectomy versus endoscopic sleeve
            gastroplasty. <em>Endosc Int Open</em>. 2021;9:E87–E95. doi:10.1055/a-1300-1085
          </li>
          <li>
            Baratte C, et al. Position statement and guidelines about Endoscopic Sleeve
            Gastroplasty. <em>J Visc Surg</em>. 2025;162:71–78. doi:10.1016/j.jviscsurg.2024.12.003
          </li>
          <li>
            Haseeb M, et al. Semaglutide vs Endoscopic Sleeve Gastroplasty for Weight Loss.{" "}
            <em>JAMA Netw Open</em>. 2024;7:e246221. doi:10.1001/jamanetworkopen.2024.6221
          </li>
        </ol>
      </EsSection>
    </EsPageShell>
  );
}
